// useScopeData — the single chokepoint that turns the active ContextKey into the scope the backend
// ethical wall reads at, and injects it into VW data calls (V1, app-platform foundation).
//
// A mounted app NEVER hand-builds `reader_scope_node_id` — it calls `useScopeData().fetchScoped(...)`
// and the hook resolves the deepest present scope level + injects the scope param. This keeps the wall
// (scope_pdp, inherit-DOWN) the single resolution authority and prevents a forgotten scope filter — the
// cross-client leak class that the data-arc egress slice was hardened against.
//
// Resolution (V1a-confirmed): the shell creates + routes scope_nodes directly (ContextChain.createScopeNode),
// so each ContextKey id IS the scope_node slug id — resolved CLIENT-SIDE, deepest present level wins. The id
// is sent as-is; the backend resolves it to a path. FAIL-CLOSED: an unknown/stale id makes the /scope-walled
// endpoints return 404 (reader node not found) — never a cross-scope leak.
import { useCallback } from "react";
import { useParams } from "@tanstack/react-router";

import type { ScopeLevel } from "@perlab/registry";

import {
  useClientId,
  useEngagementId,
  useProjectId,
  useScopeTreeStatus,
  useWorkspaceId,
} from "./ContextChain";
import { classifyFetchError } from "./denialErrors";

const API_BASE = "/api/workspace";
// The scope query-param the walled READ endpoints take (sources / retrieval / events / data-quality).
// Egress is the one exception (`requester_scope_node_id`) — override via opts.scopeParam.
const DEFAULT_SCOPE_PARAM = "reader_scope_node_id";

export type { ScopeLevel };

// Re-exported so existing consumers (DataPage.tsx, NotFoundPage.tsx, tests) keep importing the
// typed denial errors from this module — the classification logic itself lives in denialErrors.ts,
// shared with useScopedFetch.ts's getJson (single denial-mapping layer, not a duplicate path).
export {
  ConflictError,
  ForbiddenError,
  PayloadTooLargeError,
  RateLimitedError,
  ScopeSealedOrMissingError,
  ServerError,
  SessionExpiredError,
  UnsupportedMediaTypeError,
  ValidationError,
} from "./denialErrors";

export interface FetchScopedOpts {
  /** Extra query params, merged after the injected scope param. */
  query?: Record<string, string | number>;
  /** The scope query-param name. Default "reader_scope_node_id"; pass "requester_scope_node_id" for egress. */
  scopeParam?: string;
  /** Passthrough fetch init (method/body/headers). `credentials:"include"` is always applied. */
  init?: RequestInit;
}

export interface ScopeData {
  /** The scope_node_id to read at = the deepest present ContextKey level. null when scope is unresolved. */
  readerScopeNodeId: string | null;
  /** Which level resolved (display / required-level checks). null when unresolved. */
  level: ScopeLevel | null;
  /**
   * True once a walled data call is safe — which is NOT the same as "a scope id exists".
   *
   * Before the scope tree hydrates, the ContextChain scalars hold `DEFAULT_SCOPE`
   * (ContextChain.tsx:215), a build-time placeholder. On any environment that did not run
   * `seed_scope_demo.py` those nodes are not there, so `readerScopeNodeId !== null` was true
   * while the id was a phantom, and every surface fired a real request for a node the backend
   * has never heard of. Observed on the isolated DtV stack: repeated
   * `404 GET /api/workspace/app-instances?reader_scope_node_id=anand-demo`.
   *
   * A URL that names a scope is an ASSERTION and stays ready immediately — a 404 on an id the
   * caller typed is honest and worth showing. It is only the landing placeholder that waits.
   */
  ready: boolean;
  /** Walled fetch: injects the scope param + returns typed JSON. Throws ScopeUnresolvedError if !ready. */
  fetchScoped: <T>(path: string, opts?: FetchScopedOpts) => Promise<T>;
  /** Walled fetch for binary/streaming responses; preserves headers and body without JSON parsing. */
  fetchScopedResponse: (path: string, opts?: FetchScopedOpts) => Promise<Response>;
}

/** Thrown when fetchScoped is called before a scope has resolved (readerScopeNodeId is null). */
export class ScopeUnresolvedError extends Error {
  constructor() {
    super("useScopeData: no scope resolved (readerScopeNodeId is null) — cannot make a walled data call");
    this.name = "ScopeUnresolvedError";
  }
}

// Row 3.11 — the typed denial vocabulary (ForbiddenError / ScopeSealedOrMissingError /
// SessionExpiredError) and its classifier now live in denialErrors.ts, shared with
// useScopedFetch.ts's getJson — the single denial-mapping layer, not a duplicate path. Re-exported
// above for existing consumers (DataPage.tsx, NotFoundPage.tsx, tests).

export function useScopeData(): ScopeData {
  // Rules of hooks: call every selector unconditionally, then pick the deepest non-empty level.
  const workspaceId = useWorkspaceId();
  const scopeTreeStatus = useScopeTreeStatus();
  const clientId = useClientId();
  const engagementId = useEngagementId();
  const projectId = useProjectId();
  // The generic /n/$node route carries the scope node id in the URL DIRECTLY (race-free), while the
  // scalars above are synced by a POST-COMMIT effect and lag one render after navigation. On re-entry to
  // an app via the node route (e.g. the persisted app crumb) the stale scalars pointed at the PREVIOUS
  // scope → the app fetched empty data (USD 0). The URL node is the mount's truth, so prefer it — and it
  // is also the lossless free-placement scope (the exact node, not a derived scalar). Audit C1 follow-up.
  const {
    ws: routeWorkspaceId,
    client: routeClientId,
    eng: routeEngagementId,
    project: routeProjectId,
    node: routeNodeId,
  } = useParams({ strict: false }) as {
    ws?: string;
    client?: string;
    eng?: string;
    project?: string;
    node?: string;
  };

  // Every route param is synchronous URL state. Typed routes need the same protection as /n/$node:
  // ContextChain updates after commit and can otherwise inject the prior client/engagement for one request.
  const routeScopeNodeId = routeNodeId
    || routeProjectId
    || routeEngagementId
    || routeClientId
    || routeWorkspaceId;
  const readerScopeNodeId = routeScopeNodeId || projectId || engagementId || clientId || workspaceId || null;
  const contextLevel: ScopeLevel | null = projectId
    ? "project"
    : engagementId
      ? "engagement"
      : clientId
        ? "client"
        : workspaceId
          ? "workspace"
          : null;
  // A generic node's type still comes from the hydrated context graph; typed routes declare their level.
  const level: ScopeLevel | null = routeNodeId
    ? contextLevel
    : routeProjectId
      ? "project"
      : routeEngagementId
        ? "engagement"
        : routeClientId
          ? "client"
          : routeWorkspaceId
            ? "workspace"
            : contextLevel;
  // A route param naming a scope is an assertion by the caller; the ContextChain scalars, before
  // the tree settles, are DEFAULT_SCOPE and assert nothing. `error` counts as settled on purpose:
  // a failed `/scope/nodes` is not an authoritative empty tree (ContextChain.tsx:598-601), and
  // blocking every read behind a fetch that will not succeed would trade phantom 404s for a page
  // that never loads.
  const scopeAsserted = Boolean(routeScopeNodeId);
  const ready =
    readerScopeNodeId !== null && (scopeAsserted || scopeTreeStatus !== "loading");

  const fetchScopedResponse = useCallback(
    async (path: string, opts: FetchScopedOpts = {}): Promise<Response> => {
      // `ready`, not `!== null`: the docstring on fetchScoped has always said "throws
      // ScopeUnresolvedError if !ready", and the implementation checked something weaker.
      // Closing that gap is what stops the pre-hydration placeholder reaching the network.
      if (!ready) throw new ScopeUnresolvedError();
      const scopeParam = opts.scopeParam ?? DEFAULT_SCOPE_PARAM;
      const qs = new URLSearchParams();
      qs.set(scopeParam, readerScopeNodeId);
      for (const [k, v] of Object.entries(opts.query ?? {})) qs.set(k, String(v));
      const url = `${API_BASE}${path}${path.includes("?") ? "&" : "?"}${qs.toString()}`;
      const res = await fetch(url, { credentials: "include", ...opts.init });
      if (!res.ok) throw await classifyFetchError(res, `useScopeData ${path}`);
      return res;
    },
    [readerScopeNodeId, ready],
  );

  const fetchScoped = useCallback(
    async <T,>(path: string, opts: FetchScopedOpts = {}): Promise<T> => {
      const response = await fetchScopedResponse(path, opts);
      return (await response.json()) as T;
    },
    [fetchScopedResponse],
  );

  return { readerScopeNodeId, level, ready, fetchScoped, fetchScopedResponse };
}
